import axios from 'axios';
import config from 'config';
import { toast } from 'react-toastify';
import CONTENTFUL from 'constants/contentful';
import { find } from 'lodash';
import { extractEndpointName } from 'helpers/urlUtils';

const dateString = new Date().toISOString();
const setHeaders = () => {
  const headers = {};
  // Set session tracking header
  const persistedDateString = localStorage.getItem('NUL_DATE_STRING');
  if (!persistedDateString) localStorage.setItem('NUL_DATE_STRING', dateString);
  headers['x-session-start'] = dateString;
  headers['x-session-start-persisted'] = persistedDateString || dateString;
  // Set error simulation headers
  let params = new URL(document.location).searchParams;
  if (params.get('nul-simulate-error'))
    headers['x-nul-simulate-error'] = params.get('nul-simulate-error');
  return {
    baseURL: config.REACT_APP_API_URL,
    headers,
  };
};

// request error handler
const request = (args) => {
  return axios({ ...args, ...setHeaders() })
    .then((response) => {
      return handleBackendResponse(response);
    })
    .catch(handleError);
};

// GET
export const get = (url) => request({ url, method: 'get' });

// POST
export const post = (url, data, config) => request({ ...config, url, data, method: 'post' });

// PUT
export const put = (url, data) => request({ url, data, method: 'put' });

export function handleError(error) {
  const newError = parseError(error);
  debugLog(newError);
  const endpointName = extractEndpointName(newError.url);
  const code = newError?.response?.data?.code;
  const repeaterList = window.contentfulData?.[CONTENTFUL.errors]?.fields?.repeaterList ?? [];
  const findByKey = (key) => find(repeaterList, { key })?.value;
  // API may return MOOLA_* (typo), Contentful has MOOLAH_*
  const normalizedCode = code?.replace?.('MOOLA_', 'MOOLAH_') ?? code;
  const titleTranslation =
    code && (findByKey(`${normalizedCode}-title`) || findByKey(`${code}-title`));
  const subtitleTranslation =
    code && (findByKey(`${normalizedCode}-subTitle`) || findByKey(`${code}-subTitle`));
  const errorTranslation = code && (findByKey(normalizedCode) || findByKey(code));
  const userMessage =
    newError.message === 'Network Error'
      ? findByKey(newError?.message) ||
        "We couldn't connect to the server. Please check your internet or try again later."
      : titleTranslation ||
        errorTranslation ||
        newError?.response?.data?.message ||
        newError.message;

  const technicalDetails = [
    `Function: ${endpointName}`,
    `Time: ${new Date().toISOString()}`,
    newError.id && `ID: ${newError.id}`,
    newError.response?.data?.id && `Response ID: ${newError.response.data.id}`,
  ]
    .filter(Boolean)
    .map((detail, index) => (
      <div key={index} style={{ marginBottom: '2px' }}>
        {detail}
      </div>
    ));
  const structuredMessage = (
    <div>
      <div>{userMessage}</div>
      {subtitleTranslation && (
        <div style={{ fontSize: '0.9em', opacity: 0.9, marginTop: '4px' }}>
          {subtitleTranslation}
        </div>
      )}
      <div style={{ fontSize: '0.8em', opacity: 0.7, marginTop: '4px' }}>{technicalDetails}</div>
    </div>
  );

  toast.error(structuredMessage, {
    toastId: newError.url,
    autoClose: false,
  });
  return Promise.reject(newError);
}

function debugLog(message) {
  if (process.env.NODE_ENV !== 'production') console.debug(message);
}
function parseError(error) {
  const { config: { url, baseURL, data } = {}, message, id } = error; // axios call error properties
  return {
    id,
    url,
    baseURL,
    data,
    message,
    ...error,
  };
}
function handleBackendResponse(response) {
  // different type of backend errors
  const error = !!parseError(response)?.message;
  if (error) return handleError(response);

  const errors = response?.data.errors ?? response?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const firstError = errors[0];
    const syntheticError = Object.assign(
      new Error(firstError?.description || firstError?.message || firstError?.code || 'Error'),
      {
        response: {
          data: {
            code: firstError?.code,
            message: firstError?.description || firstError?.message || firstError?.code,
            id: response?.data.id || response?.data.requestId,
          },
        },
        config: response.config,
      },
    );
    return handleError(syntheticError);
  }

  return Promise.resolve(parseBackEndData(response));
}

function parseBackEndData(response) {
  const { data } = response;
  function returnAndLog(data) {
    debugLog({
      url: response.config.url,
      data,
    });
    return data;
  }
  // if a recordset array is returned
  if (data?.recordset) return returnAndLog(data.recordset);
  return returnAndLog(data);
}

export function clientRequest(url, data, config) {
  // for clientside errors we dont need to show anything to the user so undefined is fine and easy to check
  return {
    get: () => get(url).catch(() => Promise.resolve()),
    post: () => post(url, data, config).catch(() => Promise.resolve()),
  };
}
